import React from 'react';
import { Col, Form, FormModal, Input, InputNumber, Row, Select } from 'components';
import { PercentageIcon } from 'components/common/icons';
import { ValidationErrors } from 'constants/validations';
import { usePaymentTermsEditModal } from 'feature/clientTabs/payments/paymentTerms/edit/usePaymentTermsEditModal';
import { PAYMENT_TERMS } from 'staticData/form';
import { preventNonNumericValue } from 'utils/helpers';
import { Typography } from 'antd';

const { Item } = Form;

export interface IPaymentTermsEditModal {
  modalState: boolean;
  onModalStateChange: () => void;
  dataToEdit?: Record<string, any>;
}

export const PaymentTermsEditModal = (props: IPaymentTermsEditModal) => {
  const { modalState, onModalStateChange, dataToEdit } = props;
  const { onFinish, onCancel, form, contextHolder, isLoading, paymentMethodData, paymentType, handlePrepaymentChange } =
    usePaymentTermsEditModal({
      modalState,
      onModalStateChange,
      dataToEdit,
    });
  return (
    <>
      {contextHolder}
      <FormModal
        form={form}
        title={'Update Payment Terms'}
        width={680}
        okText={'Save'}
        open={modalState}
        isLoading={isLoading}
        onCancel={onCancel}
        onSubmit={onFinish}
      >
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Item
              label={'Payment Terms*'}
              name={'paymentType'}
              rules={[
                {
                  required: true,
                  message: ValidationErrors.required('Payment Terms'),
                },
              ]}
            >
              <Select size={'large'} options={PAYMENT_TERMS} />
            </Item>
          </Col>
          {paymentType !== 'PREPAID' && (
            <Col span={12}>
              <Item
                name={'threshold'}
                label={'Threshold*'}
                rules={[
                  {
                    required: true,
                    message: ValidationErrors.required('Threshold'),
                  },
                  {
                    validator: (_, value) => {
                      if (value === undefined || value === null || value === '') return Promise.resolve();
                      if (parseFloat(value) <= 0)
                        return Promise.reject(new Error('Threshold must be a positive number.'));
                      if (!/^\d+$/.test(value))
                        return Promise.reject(new Error('Threshold can only contain whole numbers, without decimals.'));

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber<number>
                  controls={false}
                  size={'large'}
                  onBeforeInput={(event: any) => preventNonNumericValue(event)}
                  style={{ width: '100%' }}
                />
              </Item>
            </Col>
          )}
          {!(paymentType === 'PREPAID' || paymentType === 'POST_PAID') && (
            <Col span={12}>
              <Item
                name={'prepayment'}
                label={'Prepayment*'}
                rules={[
                  {
                    required: true,
                    message: ValidationErrors.required('Prepayment'),
                  },
                  {
                    validator: (_, value) => {
                      if (value === undefined || value === null || value === '') return Promise.resolve();
                      const num = parseFloat(value);

                      if (isNaN(num)) return Promise.reject(new Error('Prepayment must be a valid number.'));
                      if (num <= 0) return Promise.reject(new Error('Prepayment must be a positive number.'));
                      if (num >= 100) return Promise.reject(new Error('Prepayment must be less than 100.'));
                      if (!/^\d+(\.\d{1,2})?$/.test(value))
                        return Promise.reject(new Error('Prepayment can have up to 2 decimal points.'));

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber<number>
                  controls={false}
                  size={'large'}
                  onBeforeInput={(event: any) => preventNonNumericValue(event)}
                  style={{ width: '100%' }}
                  suffix={<PercentageIcon />}
                  onChange={handlePrepaymentChange}
                />
              </Item>
            </Col>
          )}
          {!(paymentType === 'PREPAID' || paymentType === 'POST_PAID') && (
            <Col span={12}>
              <Item name={'postPayment'} label={'Post-payment'}>
                <Input size={'large'} suffix={<PercentageIcon />} disabled={true} />
              </Item>
            </Col>
          )}
          <Col span={12}>
            <Item
              name={'paymentMethodId'}
              label={'Payment Methods*'}
              rules={[
                {
                  required: true,
                  message: ValidationErrors.required('Payment Methods'),
                },
              ]}
            >
              <Select size={'large'} options={paymentMethodData} />
            </Item>
          </Col>
          {paymentType !== 'PREPAID' && (
            <Col span={12}>
              <Item
                name={'netDaysAfterDelivery'}
                label={'Net Days After Delivery*'}
                rules={[
                  {
                    required: true,
                    message: ValidationErrors.required('Net Days After Delivery'),
                  },
                  {
                    validator: (_, value) => {
                      if (value === undefined || value === null || value === '') return Promise.resolve();
                      if (parseFloat(value) <= 0)
                        return Promise.reject(new Error('Net Days After Delivery must be a positive number.'));
                      if (!/^\d+$/.test(value))
                        return Promise.reject(
                          new Error('Net Days After Delivery can only contain whole numbers, without decimals.')
                        );

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber<number>
                  controls={false}
                  size={'large'}
                  onBeforeInput={(event: any) => preventNonNumericValue(event)}
                  style={{ width: '100%' }}
                  suffix={<Typography.Text type={'secondary'}>day</Typography.Text>}
                />
              </Item>
            </Col>
          )}
        </Row>
      </FormModal>
    </>
  );
};
